import DOMPurify from 'dompurify';
import { REQUIRED_FIELD } from 'constants/forms';

// Allow-list for sanitizeHtml derived from enabled formats in the Quill editor
// (admin/src/components/QuillInput), plus tags used by legacy pages.
// Kept in sync with the twin in site/src/helpers/utils.ts.
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    'a',
    'b',
    'blockquote',
    'br',
    'center',
    'code',
    'div',
    'em',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'hr',
    'i',
    'li',
    'ol',
    'p',
    'pre',
    's',
    'small',
    'span',
    'strong',
    'sub',
    'sup',
    'u',
    'ul',
  ],
  // `class` carries every Quill format that isn't a tag of its own (e.g. ql-align-*)
  // Quill sets target="_blank" and rel="noopener noreferrer" on every link
  // it creates, so dropping those two would change existing link behavior.
  // Deliberately absent: `id` (DOM clobbering).
  ALLOWED_ATTR: ['class', 'href', 'rel', 'target', 'title', 'style'],
};

// `style` is allowed only to carry text-align, which legacy pages rely on for layout.
// Every other declaration is stripped.
const ALLOWED_STYLE_DECLARATION = /^text-align:\s*(left|right|center|justify)$/i;

DOMPurify.addHook('afterSanitizeAttributes', node => {
  const style = node.getAttribute?.('style');
  if (!style) return;

  const kept = style
    .split(';')
    .map(declaration => declaration.trim())
    .filter(declaration => ALLOWED_STYLE_DECLARATION.test(declaration));

  if (kept.length) {
    node.setAttribute('style', `${kept.join('; ')};`);
  } else {
    node.removeAttribute('style');
  }
});

export const sanitizeHtml = (html: string | null | undefined): string =>
  DOMPurify.sanitize(html ?? '', SANITIZE_CONFIG);

export const numberWithDot = (x: number | string) => {
  const withComma = x?.toString().replace(',', '.');

  return withComma?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') || ' ...';
};

export const percentDiff = (val1: number, val2: number) => {
  if (val1 && val2) return ((val1 * 100) / val2 - 100).toFixed(2);
  return '-';
};

export const sumAmounts = (array: number[]) => {
  return array?.reduce((accumulator, value) => {
    return accumulator + value;
  }, 0);
};

export const limitChars = (value, limit) => {
  if (value?.length > limit) {
    return `${value.substring(0, limit)}...`;
  } else {
    return value;
  }
};

export const handleTrimOnBlur = (
  e: React.FocusEvent<HTMLInputElement>,
  input: {
    onChange: (value: string) => void;
    onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  }
) => {
  const trimmedValue = e.target.value.trim();
  input.onChange(trimmedValue);
  input.onBlur(e);
};

export const transformToUrl = (value: string) => {
  return value.replace(/ /g, '-');
};

// Only produce an href for http(s) URLs so a hostile value (e.g. javascript:) can't run on click.
// When allowedHosts is given, also require the host to be one of them — defense-in-depth for
// links that should only ever point at known domains.
export const safeHref = (url: string, allowedHosts?: readonly string[]): string | undefined => {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    // Schemeless input (e.g. "example.org"): retry as https. Anything with a real scheme
    // parses above, so a rejected javascript:/data: value is never upgraded here.
    try {
      parsed = new URL(`https://${url.trim()}`);
    } catch {
      return undefined;
    }
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return undefined;
  if (allowedHosts && !allowedHosts.includes(parsed.hostname)) return undefined;
  // Return the parsed href so a schemeless value resolves to an absolute URL, not a relative one.
  return parsed.href;
};

export function wait(timeout) {
  return new Promise(resolve => {
    setTimeout(resolve, timeout);
  });
}

export const getUrlSearchParamsObj = () => new URL(window.location.href).searchParams;

export const getUrlParams = () => {
  const searchParams = getUrlSearchParamsObj();

  return {
    tagId: searchParams?.get('tag_id'),
    copy: searchParams?.get('copy'),
    pageType: searchParams?.get('page_type'),
    setId: searchParams?.get('set_id'),
  };
};

export const isKeyInObject = (key: string, obj: object) => Object.keys(obj).includes(key);

// Compare ids that may differ in type (number vs string); null/undefined never match.
export const sameId = (
  a: string | number | null | undefined,
  b: string | number | null | undefined
): boolean => a != null && b != null && String(a) === String(b);

export const getInitials = (fullName: string) => {
  const words = fullName.trim().split(/\s+/);

  // Get the first two words and their initials
  const initials = words
    .slice(0, 2)
    .map(word => word[0])
    .join('');

  return initials.toUpperCase();
};

export const formatNumberAsCurrency = (num: number, options: object = {}) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    //@ts-ignore
    trailingZeroDisplay: 'stripIfInteger',
    ...options,
  }).format(num);
};

export const parseCurrency = (value: string): number =>
  parseFloat(value.replace(/[^0-9.-]+/g, '')) || 0;

export const dollarsToCents = (dollars: number): number => Math.round(dollars * 100);

export const formatCurrencyAbbreviated = (num?: number): string => {
  if (num === undefined || num === null) return '$ ...';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumSignificantDigits: 3,
  }).format(num);
};

export const required = (value: any) => (value ? undefined : REQUIRED_FIELD);

export const validateEin = (value: string) => {
  if (!value) return undefined;
  return /^\d{9}$/.test(value) ? undefined : 'EIN must be exactly 9 digits.';
};

export const requiredNumber = (value: any) => {
  if (value === undefined || value === null || value === '') {
    return REQUIRED_FIELD;
  }

  const isDigitsOnly = /^\d+$/.test(value);

  return isDigitsOnly ? undefined : 'This field must contain digits only.';
};

export const caseInsensitiveSort = (textA: string, textB: string) => {
  const valueA = textA?.toLowerCase() || '';
  const valueB = textB?.toLowerCase() || '';
  return valueA.localeCompare(valueB);
};

export const createReverseMap = <K, V>(originalMap: Map<K, V>): Map<V, K> => {
  const reversedEntries = Array.from(originalMap, ([key, value]) => [value, key] as [V, K]);

  return new Map(reversedEntries);
};

export const buildQueryParams = (params: Record<string, any>): string => {
  const paramStrings: string[] = [];

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      // Handle array values (for multi-value params like recipient_ids)
      if (Array.isArray(value)) {
        value.forEach(item => {
          paramStrings.push(`${key}=${item}`);
        });
      } else {
        paramStrings.push(`${key}=${value}`);
      }
    }
  });

  return paramStrings.length > 0 ? `?${paramStrings.join('&')}` : '';
};

export const capitalizeUpperText = (text: string): string => {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

/**
 * Get the resource names of the resources an admin user has access to
 * @param resource_access the resource access object from the getAdminUsers API response
 * @returns a list of strings, each string is a user's assigned role and the associated resources
 */

export const getResourcesFromResourceAccess = (resource_access: object): string[] => {
  const fullResourceList: string[] = [];

  Object.keys(resource_access).forEach(key => {
    const resourcesList: string[] = [];

    const resources = resource_access[key].resources;
    for (const resource of resources) {
      resourcesList.push(resource.resource_name);
    }
    const roleToResourcesList = `${key}:  ${resourcesList.join(', ')}`;

    fullResourceList.push(roleToResourcesList);
  });
  return fullResourceList;
};
